import { useState } from 'react';
import { DropdownOption, StudentRow } from './types';

type Severity = 'success' | 'info' | 'warn' | 'error';
type Trigger = (arg: any) => { unwrap: () => Promise<any> };

interface Deps {
  schoolId: number;
  savePayloadMeta: { user_type: string };
  studentJsonData: StudentRow[];
  allClassSectionData: any[];
  getGroupName: (stu: any) => string;
  refetchExposure: () => void;
  addExposureStudent: Trigger;
  notify: (s: Severity, summary: string, detail: string) => void;
}

// Add-Student dialog state and handlers.
export function useExposureDialogs(d: Deps) {
  const [displayDialog, setDisplayDialog] = useState(false);
  const [sectionListArr, setSectionListArr] = useState<DropdownOption[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<any[]>([]);
  const [dialogSearch, setDialogSearch] = useState('');
  const [dialogStudentList, setDialogStudentList] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<any>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);

  function addStudent() {
    setSelectedClass(12);
    setSelectedSection(null);
    setDialogStudentList([]);
    setSelectedStudents([]);
    setDialogSearch('');
    const sections = d.allClassSectionData
      .filter((i) => i.class_studying_id == 12)
      .map((l) => l.class_section || l.section);
    setSectionListArr(
      [...new Set(sections)]
        .filter(Boolean)
        .map((s) => ({ label: s as string, value: s as string }))
    );
    setDisplayDialog(true);
  }

  function onSectionChange(value: string) {
    setSelectedSection(value);
    setSelectedStudents([]);
    if (!selectedClass || !value) return setDialogStudentList([]);
    const existing = new Set(d.studentJsonData.map((r) => String(r.user_id)));
    const inSection = (stu: any) =>
      stu.class_studying_id == selectedClass &&
      (stu.class_section == value || stu.section == value);
    setDialogStudentList(
      d.allClassSectionData.filter(inSection).map((stu) => ({
        ...stu,
        group_name: d.getGroupName(stu),
        alreadyAdded: existing.has(String(stu.user_id)),
      }))
    );
  }

  async function onAddSelected() {
    if (!selectedStudents.length)
      return d.notify('warn', 'Warning', 'Please select at least one student.');
    try {
      const records = (stu: any) => ({
        user_id: Number(stu.user_id),
        school_id: Number(d.schoolId),
        is_dump: '0',
        ...d.savePayloadMeta,
      });
      const results = await Promise.all(
        selectedStudents.map(async (stu) => ({
          stu,
          res: await d.addExposureStudent({ records: records(stu) }).unwrap(),
        }))
      );
      const failed = results.filter(({ res }) => !res?.dataStatus);
      failed.forEach(({ stu, res }) =>
        d.notify(
          'warn',
          `${stu.name ?? stu.user_id}`,
          res?.message || 'Record already exists for this student'
        )
      );
      const ok = results.length - failed.length;
      if (ok) d.notify('success', 'Success', `${ok} student(s) added.`);
      d.refetchExposure();
      setSelectedStudents([]);
      if (!failed.length) setDisplayDialog(false);
    } catch {
      d.notify('error', 'Error', 'Something went wrong. Please try again.');
    }
  }

  return {
    displayDialog,
    setDisplayDialog,
    sectionListArr,
    selectedStudents,
    setSelectedStudents,
    dialogSearch,
    setDialogSearch,
    dialogStudentList,
    selectedClass,
    selectedSection,
    onSectionChange,
    onAddSelected,
    addStudent,
  };
}
